import { useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";
import {
  useEkipSchedule,
  useAcceptShift,
  useCompleteShift,
  useSaveWorkInfo,
  type EkipShift,
  type EkipDayGroup,
} from "./api";
import { useAuthV2 as useAuth } from "@/lib/authv2/store";
import { useDataVisibility } from "@/lib/authv2/permissions";
import { StaffAvatar } from "@/components/StaffAvatar";
import { ChartCard } from "@/features/dashboard/primitives/ChartCard";
import { vnd } from "@/features/dashboard/lib/format";
import { PhotoConfirmButton } from "./PhotoConfirmButton";
import { canMakeupUploadBeforeShoot, canMakeupWorkShift, isShiftOpen } from "./shiftWindow";
import { BeforeAfterBlock } from "@/features/proofs/BeforeAfterBlock";
import { MakeupEarlyHint } from "@/features/proofs/MakeupEarlyHint";
import { MyMakeupFacesBlock } from "./MyMakeupFacesBlock";
import { DeliveryProofPanel } from "@/features/proofs/DeliveryProofPanel";
import { useOrderProofs } from "@/features/proofs/api";
import { DeliverButton } from "@/features/proofs/DeliverButton";
import { useDeliverNeedsPhoto } from "@/features/proofs/useDeliverNeedsPhoto";
import { hasMakeupPair, isIdPhotoOrder } from "@/features/proofs/utils";
import { useConceptView } from "@/features/lookbook/useConceptView";
import { ConceptBlock } from "@/features/orders/components/ConceptBlock";
import { STATUS_MAP } from "@/features/orders/statusMap";
import {
  useCrewAcceptOnAssign,
  useLineLabel,
  useProductNameMap,
} from "@/features/products/catalogApi";
import { useBranchLabel } from "@/lib/branches";
import { formatDate } from "@/lib/format";
import { rawLinkHrefFor, rawLinkLabel } from "@/lib/api/rawLink";
import { canCorrectLockedEkipInfo, workInfoLockNotice } from "./lockPermissions";
import { useMyClaimedOrderIds } from "@/features/ekip-pool/api";
import {
  canEditRawPhotoLinks,
  rawPhotoLockNotice,
  type LinkUser,
} from "@/features/schedule/components/detail/rawPhotoPermissions";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/ui/checkbox";
import { TAG_DAC_BIET, TAG_SUA_CAN_THAN, warnTags } from "@/features/orders/tags";
import { WarnTagChips } from "@/features/orders/components/WarnTags";
import { DayWarnFlag, ShiftWarnGuide } from "./ShiftWarnGuide";
import { OrderOccasionRow } from "@/features/occasion/OrderOccasionRow";
import { HintNote } from "@/components/HintTip";
import { SaleOrSquad } from "@/features/sales-squad/SaleOrSquad";

/**
 * Tiền phải thu của ca = giá gói sau giảm giá đã gộp VAT + phát sinh đã gộp
 * VAT từng dòng (HRS-053). `total` là giá gói GỐC nên không dùng làm số này.
 * Đơn cũ chưa có snapshot thì MÁY CHỦ đã lùi về gói sau giảm giá
 * (payment.PayableCoreExprSQL) — cùng một số với hộp thu tiền và thẻ Tổng công
 * nợ. Trước 28/09/2026 màn này tự lùi về `total` (giá gốc) nên một đơn cũ ra
 * ba con số ở ba màn.
 */
export function shiftBillable(o: EkipShift): number {
  return o.payableTotal + o.surchargesTotal;
}

/**
 * Khách còn nợ = phải thu − cọc − tiền đã trả vào. `paid` đã trừ khoản hoàn nên
 * cộng lại `refunded`: khoản hoàn không sinh công nợ (lỗi #01 kiểm thử 16/09/2026).
 */
export function shiftRemaining(o: EkipShift): number {
  return Math.max(0, shiftBillable(o) - o.deposit - o.paid - (o.refunded ?? 0));
}

/**
 * EkipSchedule — port FE cũ (618 LOC) cô đọng.
 *
 * Hai phần:
 *   1. Personal timeline today — list các ca hôm nay với marker NOW
 *   2. 7-day workload accordion — grid 7 thẻ ngày, click ngày có ca →
 *      mở list ca → click 1 ca → mở tier-2 chi tiết với action buttons
 *      (Nhận ca / Hoàn tất chụp với 5s countdown).
 *
 * Toàn bộ confirm-state lấy từ BE (`isMine` / `myAccept` / `myConfirm` /
 * `isPhoto`) → FE không cần derive lại.
 */
export function EkipSchedule() {
  const me = useAuth((s) => s.currentStaff);
  const q = useEkipSchedule();
  const dept = (me?.department ?? "").toLowerCase();
  const isPhotoOrMakeup = dept.startsWith("photo") || dept === "makeup";
  const claimedOrderIds = useMyClaimedOrderIds();
  const viewer = useMemo<WorkInfoViewer>(() => ({ me, claimedOrderIds }), [me, claimedOrderIds]);

  if (q.isLoading) {
    return (
      <ChartCard title="Lịch ekip của tôi">
        <div className="text-sm text-muted-foreground">Đang tải lịch…</div>
      </ChartCard>
    );
  }
  if (q.error || !q.data) {
    return (
      <ChartCard title="Lịch ekip của tôi">
        <div className="text-sm text-destructive">
          {(q.error as Error | undefined)?.message ?? "Không tải được lịch"}
        </div>
      </ChartCard>
    );
  }

  return (
    <div className="space-y-4">
      <OverdueProofBanner shifts={q.data.overdueProofShifts ?? []} />
      <PersonalTimeline shifts={q.data.todayShifts ?? []} />
      <WeekAccordion
        weekDays={q.data.weekDays ?? []}
        today={q.data.today}
        canAct={isPhotoOrMakeup}
        viewer={viewer}
      />
    </div>
  );
}

// ════════════════ Ca cũ còn nợ ảnh nghiệm thu ════════════════

/**
 * OverdueProofBanner — ca ĐÃ QUA mà tôi còn thiếu ảnh trước/sau trang điểm và
 * vẫn còn hạn nộp (quyết định 0192).
 *
 * Vì sao phải có khối riêng: lưới 7 ngày bắt đầu từ HÔM NAY, nên qua nửa đêm là
 * thẻ ca của hôm qua biến mất khỏi màn hình — người trang điểm về muộn không
 * còn chỗ nào để bấm, dù máy chủ vẫn nhận ảnh thêm 24 giờ nữa.
 *
 * Dùng lại nguyên ActionButtons: hai ô ảnh và nút hoàn tất phải là CÙNG một
 * đường với thẻ ca thường, không phải bản chép lại.
 */
export function OverdueProofBanner({ shifts }: { shifts: EkipShift[] }) {
  if (shifts.length === 0) return null;
  return (
    <div className="rounded-lg border-2 border-[var(--color-warning)] bg-[var(--color-warning-bg)] p-4">
      <div className="mb-1 text-sm font-bold text-[var(--color-warning-text)]">
        Ca đã qua — còn thiếu ảnh nghiệm thu trang điểm
      </div>
      <div className="mb-3 text-[11px] text-[var(--color-warning-text)]/85">
        Nộp đủ ảnh trước và sau trước hạn thì ca vẫn đóng được. Quá hạn là thôi — không mở lại
        được nữa.
      </div>
      <div className="space-y-3">
        {shifts.map((o) => (
          <div key={o.id} className="rounded-md border border-[var(--color-warning)]/50 bg-surface p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="text-xs font-bold text-foreground">{o.customer}</span>
              <span className="text-[11px] text-muted-foreground">
                Ca {formatDate(o.shootDate)}
                {o.startTime ? ` · ${o.startTime}` : ""}
              </span>
            </div>
            <ProofDeadlineNote dueAt={o.makeupProofDueAt} />
            <ActionButtons order={o} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Dòng "còn bao lâu" — nói mốc thật, không bắt người đọc tự tính. */
function ProofDeadlineNote({ dueAt }: { dueAt: string | null | undefined }) {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;
  return (
    <div className="mt-0.5 text-[11px] font-semibold text-[var(--color-warning-text)]">
      Hạn nộp: {due.toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
    </div>
  );
}

// ════════════════ Personal timeline today ════════════════

function PersonalTimeline({ shifts }: { shifts: EkipShift[] }) {
  const nowStr = useMemo(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }, []);

  const sorted = useMemo(
    () => [...shifts].sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? "")),
    [shifts],
  );

  return (
    <div className="rounded-lg border border-[var(--color-success)]/40 bg-gradient-to-br from-[var(--color-success-bg)] to-[var(--color-success-bg)]/40 p-4">
      <div className="mb-2.5 flex items-center justify-between">
        <div className="text-sm font-bold text-[var(--color-success-text)]">
          Lịch của tôi hôm nay — {shifts.length} ca
        </div>
        <span className="text-[11px] font-semibold text-[var(--color-success-text)]/80">
          {nowStr}
        </span>
      </div>
      {sorted.length === 0 ? (
        <div className="py-3 text-center text-xs text-[var(--color-success-text)]/80">
          Không có ca hôm nay
        </div>
      ) : (
        <div className="space-y-1">
          {sorted.map((o) => (
            <TimelineRow key={o.id} order={o} nowStr={nowStr} />
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineRow({ order: o, nowStr }: { order: EkipShift; nowStr: string }) {
  const { nameOf: conceptName } = useConceptView();
  const lineLabel = useLineLabel();
  const oTime = o.startTime ?? "99:99";
  const oEnd = o.endTime ?? "";
  const isPast = oEnd ? oEnd < nowStr : oTime < nowStr;
  const isCurrent = !isPast && oTime <= nowStr;

  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-md p-2 transition",
        isCurrent
          ? "border-2 border-[var(--color-success)] bg-[var(--color-success)]/10"
          : isPast
            ? "border border-transparent bg-black/5 opacity-60"
            : "border border-transparent bg-white/70",
      )}
    >
      <div className="w-14 shrink-0 text-center">
        <div
          className={cn(
            "text-sm font-extrabold",
            isCurrent ? "text-[var(--color-success-text)]" : "text-foreground",
          )}
        >
          {o.startTime ?? "—"}
        </div>
        {oEnd ? <div className="text-[9px] text-muted-foreground">{oEnd}</div> : null}
        {isPast ? <div className="text-[9px] text-muted-foreground">✓</div> : null}
        {isCurrent ? (
          <div className="text-[9px] font-bold text-[var(--color-success-text)]">NOW</div>
        ) : null}
      </div>
      <div
        className={cn(
          "h-6 w-[3px] shrink-0 rounded",
          isCurrent
            ? "bg-[var(--color-success)]"
            : isPast
              ? "bg-[var(--color-success)]/30"
              : "bg-[var(--color-success)]/60",
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-foreground">{o.customer}</div>
        <div className="text-[11px] text-[var(--color-success-text)]/80">
          {lineLabel(o.category)}
          {o.selectedConcept ? ` • ${conceptName(o.selectedConcept)}` : ""}
        </div>
      </div>
      <span className="shrink-0 rounded-md bg-surface px-2 py-0.5 text-[9px] font-bold text-muted-foreground">
        {STATUS_MAP[o.status] ?? o.status}
      </span>
    </div>
  );
}

// ════════════════ 7-day accordion ════════════════

function WeekAccordion({
  weekDays,
  today,
  canAct,
  viewer,
}: {
  weekDays: EkipDayGroup[];
  today: string;
  canAct: boolean;
  viewer: WorkInfoViewer;
}) {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const handleDayClick = (i: number) => {
    setSelectedDay((cur) => (cur === i ? null : i));
    setSelectedOrderId(null);
  };

  const selectedDayOrders = useMemo(() => {
    if (selectedDay === null) return [];
    return weekDays[selectedDay]?.orders ?? [];
  }, [selectedDay, weekDays]);

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-2.5 text-sm font-bold text-foreground">Lịch 7 ngày tới</div>

      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 gap-1.5 min-w-[420px]">
          {weekDays.map((wd, i) => (
            <DayCell
              key={wd.date}
              wd={wd}
              isToday={i === 0}
              isSelected={selectedDay === i}
              onClick={() => (wd.count > 0 ? handleDayClick(i) : null)}
            />
          ))}
        </div>
      </div>

      {selectedDay !== null && selectedDayOrders.length > 0 ? (
        <div className="mt-3 border-t border-border pt-3">
          <div className="mb-2 text-[11px] font-bold text-[var(--color-info-text)]">
            {weekDays[selectedDay]?.label} — {weekDays[selectedDay]?.day}/
            {monthOf(weekDays[selectedDay]?.date ?? "")} — {selectedDayOrders.length} ca
          </div>
          {selectedDayOrders.map((o) => (
            <ShiftRow
              key={o.id}
              order={o}
              expanded={selectedOrderId === o.id}
              onToggle={() => setSelectedOrderId((cur) => (cur === o.id ? null : o.id))}
              canAct={canAct}
              viewer={viewer}
            />
          ))}
        </div>
      ) : null}

      {selectedDay === null ? <UpcomingPreview weekDays={weekDays} today={today} /> : null}
    </div>
  );
}

function DayCell({
  wd,
  isToday,
  isSelected,
  onClick,
}: {
  wd: EkipDayGroup;
  isToday: boolean;
  isSelected: boolean;
  onClick: () => void;
}) {
  const hasShift = wd.count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasShift}
      className={cn(
        "relative rounded-lg p-2 text-center transition",
        isSelected
          ? "scale-[1.05] border-2 border-[var(--color-info)] bg-[var(--color-info-bg)]"
          : isToday
            ? "border-2 border-[var(--color-info)] bg-[var(--color-info-bg)]/60"
            : hasShift
              ? "border border-[var(--color-success)]/40 bg-[var(--color-success-bg)]/40"
              : "border border-border bg-[var(--color-surface-2)]",
        hasShift ? "cursor-pointer" : "cursor-default",
      )}
    >
      <div
        className={cn(
          "text-[10px] font-bold",
          isSelected || isToday ? "text-[var(--color-info-text)]" : "text-muted-foreground",
        )}
      >
        {wd.label}
      </div>
      <div className="text-[9px] text-muted-foreground">
        {wd.day}/{monthOf(wd.date)}
      </div>
      <div
        className={cn(
          "mt-1 text-lg font-extrabold",
          wd.count === 0
            ? "text-[var(--color-text-muted)]"
            : wd.count >= 3
              ? "text-[var(--color-danger-text)]"
              : wd.count >= 2
                ? "text-[var(--color-warning-text)]"
                : "text-[var(--color-success-text)]",
        )}
      >
        {wd.count}
      </div>
      <div className="text-[9px] text-muted-foreground">ca</div>
      {/* Last, so the day's name still opens its accessible name. */}
      <DayWarnFlag orders={wd.orders} />
    </button>
  );
}

function monthOf(date: string): string {
  if (!date) return "";
  const m = Number(date.slice(5, 7));
  return String(m);
}

function ShiftRow({
  order: o,
  expanded,
  onToggle,
  canAct,
  viewer,
}: {
  order: EkipShift;
  expanded: boolean;
  onToggle: () => void;
  canAct: boolean;
  viewer: WorkInfoViewer;
}) {
  const lineLabel = useLineLabel();
  const { nameOf: conceptName } = useConceptView();
  const acceptOnAssign = useCrewAcceptOnAssign();
  const status = pickShiftStatus(o, acceptOnAssign(o.category));
  const warn = warnTags(o.tags, o.isKol);
  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-md p-2 text-left transition",
          expanded
            ? "border-2 border-[var(--color-info)] bg-[var(--color-info-bg)]"
            : "border border-border bg-[var(--color-surface-2)] hover:border-[var(--color-info)]/60",
        )}
      >
        <div className="w-12 shrink-0 text-xs font-bold text-foreground">{o.startTime ?? "—"}</div>
        <div className="h-6 w-[2px] shrink-0 rounded bg-[var(--color-border)]" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-foreground">{o.customer}</div>
          <div className="truncate text-[10px] text-muted-foreground">
            {conceptName(o.selectedConcept) || lineLabel(o.category)}
          </div>
          {/* Phones: under the name, where the narrow row still has room. */}
          <WarnTagChips labels={warn} size="sm" className="mt-1 flex md:hidden" />
        </div>
        {/* Wider screens: right before the status chip. */}
        <WarnTagChips labels={warn} size="sm" className="hidden shrink-0 md:inline-flex" />
        <span className={cn("shrink-0 rounded-md px-2 py-0.5 text-[9px] font-bold", status.tone)}>
          {status.label}
        </span>
        <span
          className={cn(
            "shrink-0 text-xs text-muted-foreground transition-transform",
            expanded ? "rotate-180" : "",
          )}
        >
          ▾
        </span>
      </button>

      {expanded ? (
        <ShiftDetail order={o} canAct={canAct} viewer={viewer} />
      ) : null}
    </div>
  );
}

/**
 * `acceptOnAssign` — dòng của đơn bật "được xếp là nhận ca" (quyết định 0234):
 * có tên trong ca là đã nhận, không bao giờ treo "Chờ nhận ca".
 */
function pickShiftStatus(
  o: EkipShift,
  acceptOnAssign = false,
): { label: string; tone: string } {
  if (!o.isMine) {
    return {
      label: STATUS_MAP[o.status] ?? o.status,
      tone: "bg-[var(--color-surface-3)] text-muted-foreground",
    };
  }
  if (o.myConfirm) {
    return {
      label: "Đã xác nhận",
      tone: "bg-[var(--color-success-bg)] text-[var(--color-success-text)]",
    };
  }
  if (o.myAccept || acceptOnAssign) {
    return {
      label: "Đã nhận ca",
      tone: "bg-[var(--color-info-bg)] text-[var(--color-info-text)]",
    };
  }
  return {
    label: "Chờ nhận ca",
    tone: "bg-[var(--color-warning-bg)] text-[var(--color-warning-text)]",
  };
}

// ════════════════ Tier-2 detail ════════════════

function ShiftDetail({
  order: o,
  canAct,
  viewer,
}: {
  order: EkipShift;
  canAct: boolean;
  viewer: WorkInfoViewer;
}) {
  // QĐ 0132 câu 1a: thợ không được xem tiền thì khối Tài chính bỏ hẳn — máy
  // chủ đã trả 0 nhưng màn hình gác theo cờ, không theo giá trị.
  const { orderMoney } = useDataVisibility();
  return (
    <div className="my-1 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-surface-2)] p-4">
      <div className="mb-3 text-sm font-bold text-foreground">Chi tiết: {o.customer}</div>

      <ShiftWarnGuide order={o} />

      <InfoGrid order={o} />

      {/* Dịp chụp (QĐ 0480, 9a): ekip gần khách nhất — khách đang ngồi thì hỏi một câu. */}
      <div className="mt-2.5 rounded-md border border-border bg-surface p-2.5">
        <div className="mb-1 text-[11px] font-bold text-foreground">Dịp chụp</div>
        <OrderOccasionRow orderId={o.id} suggestText={o.notes} compact />
      </div>

      {o.notes ? (
        <div className="mt-2.5 rounded-md border border-[var(--color-border)] bg-[var(--color-cream,#FFFDF5)]/60 p-2.5">
          <div className="mb-1 text-[11px] font-bold text-[var(--color-brand)]">Ghi chú</div>
          <div className="whitespace-pre-wrap text-[11px] leading-relaxed text-foreground/90">
            {o.notes}
          </div>
        </div>
      ) : null}

      {orderMoney && (shiftBillable(o) > 0 || o.deposit > 0) ? (
        <div className="mt-2.5 rounded-md border border-border bg-surface p-2.5">
          <div className="mb-1 text-[11px] font-bold text-foreground">Tài chính</div>
          <div className="grid gap-1 text-[11px]">
            <Row k="Tổng cộng" v={vnd(shiftBillable(o))} />
            {o.deposit > 0 ? <Row k="Tiền cọc" v={vnd(o.deposit)} success /> : null}
            {shiftRemaining(o) > 0 ? (
              <div className="flex justify-between border-t border-border pt-1 font-bold text-[var(--color-danger-text)]">
                <span>Còn lại</span>
                <span>{vnd(shiftRemaining(o))}</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {canAct ? <ActionButtons order={o} /> : null}
      {/* Ảnh bàn giao tải được từ lúc thợ chụp được xếp ca, kể cả sau khi đã
          giao để chụp bổ sung (CEO chốt 29/09/2026). */}
      {canAct &&
      o.isMine &&
      o.isPhoto &&
      o.status !== "cancelled" &&
      isIdPhotoOrder({ category: o.category, product: o.product }) ? (
        <DeliveryProofPanel orderId={o.id} compact />
      ) : null}
      {canAct && o.isMine ? (
        <RawLinksEditor order={o} viewer={viewer} />
      ) : null}
    </div>
  );
}

// ════════════════ Raw photo links (link gốc) ════════════════

/** Hai thẻ ê-kíp tick được trong hộp Công việc — thứ tự = thứ tự ô tick. */
const EKIP_TAGS = [TAG_SUA_CAN_THAN, TAG_DAC_BIET] as const;

/**
 * Ai đang xem, cộng suất họ đang giữ — đủ để dựng HAI mức khoá của hộp Công
 * việc.
 *
 * Gói làm một để đường truyền qua bốn tầng component không phình thêm prop:
 * trước 25/09/2026 chỗ này là `canEditLockedWorkInfo: boolean`, nhưng cờ ấy chỉ
 * là hệ quả của `me`, nên truyền chính `me` còn hơn truyền cả hai rồi để chúng
 * lệch nhau.
 */
export type WorkInfoViewer = {
  me: LinkUser;
  /**
   * Đơn mình đã giơ tay nhận suất. Người thứ hai của một suất đang tranh CHƯA
   * có tên trên đơn mà vẫn làm buổi đó, nên thiếu tập này là khoá oan đúng
   * những người máy chủ vẫn cho ghi (gương của `ActorOnSlot` →
   * `hasActiveClaimSlot`).
   */
  claimedOrderIds: ReadonlySet<string>;
};

// export chỉ để kiểm thử (RawLinksEditor.tags.test.tsx); màn khác không dùng.
export function RawLinksEditor({
  order: o,
  viewer,
}: {
  order: EkipShift;
  viewer: WorkInfoViewer;
}) {
  const initial = useMemo(() => o.rawPhotoLinks ?? [], [o.rawPhotoLinks]);
  const initialNotes = o.notes ?? "";
  const [notes, setNotes] = useState(initialNotes);
  const [links, setLinks] = useState<string[]>(initial);
  const initialTags = useMemo<string[]>(
    () => EKIP_TAGS.filter((t) => (o.tags ?? []).includes(t)),
    [o.tags],
  );
  const [tags, setTags] = useState<string[]>(initialTags);
  const [draft, setDraft] = useState("");
  const save = useSaveWorkInfo();

  // HAI mức khoá, không còn một cờ chung (quyết định 0196, mục Follow-Up).
  // Ghi chú và thẻ hậu kỳ giữ mốc cũ "ca phải đang chụp". Ô link ảnh gốc theo
  // mốc NGÀY CHỤP, y như hai màn kia (Lịch chụp, trang Đơn hàng) — gọi CHUNG
  // hàm luật của chúng, không suy lại lần nữa ở đây.
  const lockedWorkInfo = o.status !== "shooting" && !canCorrectLockedEkipInfo(viewer.me);
  const hasActiveClaim = viewer.claimedOrderIds.has(o.id);
  const lockedRawLinks = !canEditRawPhotoLinks(viewer.me, o, hasActiveClaim);
  const rawLinksNotice = rawPhotoLockNotice(o, viewer.me, hasActiveClaim);

  const workInfoDirty =
    notes !== initialNotes ||
    tags.length !== initialTags.length ||
    tags.some((t) => !initialTags.includes(t));
  const linksDirty = links.length !== initial.length || links.some((l, i) => l !== initial[i]);
  // Nút Lưu chỉ hiện khi có thay đổi LƯU ĐƯỢC: đổi ở ô đang khoá thì không
  // tính, nếu không người ta bấm Lưu rồi ăn 403 mà không hiểu vì sao.
  const dirty = (workInfoDirty && !lockedWorkInfo) || (linksDirty && !lockedRawLinks);

  function addDraft() {
    const v = draft.trim();
    if (!v) return;
    setLinks((prev) => [...prev, v]);
    setDraft("");
  }

  /**
   * Gửi ĐÚNG phần đang mở, không gửi cả ba như trước.
   *
   * Cửa ngày chụp của máy chủ (`rawPhotoOnlyPatchOnShootDay`) chỉ nhận bản vá
   * CHỈ mang link — nó đòi `Notes == nil && Tags == nil`. Kèm ghi chú hay thẻ
   * vào lượt ấy là ăn 403 "Ca đã khóa", đúng cái tắc mục này đang gỡ.
   */
  async function persist(next: string[]) {
    try {
      await save.mutateAsync({
        orderId: o.id,
        ...(lockedRawLinks ? {} : { links: next }),
        ...(lockedWorkInfo ? {} : { notes, tags }),
      });
      toast.success("Đã lưu thông tin ca");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Lưu thông tin ca thất bại");
    }
  }

  return (
    <div className="mt-2.5 rounded-md border border-[var(--color-info)]/30 bg-[var(--color-info-bg)]/40 p-2.5">
      <label className="mb-2 block">
        <span className="mb-1 block text-xs font-bold text-[var(--color-info-text)]">
          Ghi chú ca chụp
        </span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={lockedWorkInfo}
          placeholder="Nhập ghi chú sau buổi chụp..."
          className="min-h-20 w-full rounded-md border bg-surface px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[var(--color-info)]/30"
        />
      </label>

      <fieldset className="mb-2">
        <legend className="mb-1 text-xs font-bold text-[var(--color-info-text)]">
          Thẻ cho phòng hậu kỳ
          {o.isKol ? (
            <span
              className="ml-2 rounded px-1.5 py-0.5 text-[11px] font-semibold text-white"
              style={{ background: "#E91E63" }}
            >
              ⭐ KOL
            </span>
          ) : null}
        </legend>
        <div className="flex flex-wrap gap-3">
          {EKIP_TAGS.map((t) => {
            const id = `ekip-tag-${o.id}-${t === TAG_DAC_BIET ? "dac-biet" : "sua-can-than"}`;
            return (
              <label key={t} htmlFor={id} className="flex items-center gap-1.5 text-xs">
                <Checkbox
                  id={id}
                  aria-label={t}
                  checked={tags.includes(t)}
                  disabled={lockedWorkInfo}
                  onCheckedChange={(v) =>
                    setTags((prev) => (v === true ? [...prev, t] : prev.filter((x) => x !== t)))
                  }
                />
                {t}
              </label>
            );
          })}
        </div>
        <HintNote className="mt-1">
          Tick khi khách khó / ảnh cần sửa kỹ — đơn sẽ tới đúng người phụ trách loại này.
        </HintNote>
      </fieldset>

      {lockedWorkInfo ? (
        <LockNote text={workInfoLockNotice(o.status)} />
      ) : null}

      <div className="mb-1.5 text-xs font-bold text-[var(--color-info-text)]">
        📎 Link gốc ({links.length})
      </div>

      {links.length > 0 && (
        <ul className="mb-2 space-y-1">
          {links.map((l, i) => (
            <li key={i} className="flex items-center gap-1.5 text-xs">
              {/* Link đã lưu vào đơn mở QUA BE để ghi sổ (M4), text chỉ hiện
                  hostname. Link vừa dán chưa lưu chưa có trên server — mở
                  thẳng tạm cho tới khi bấm Lưu (người dán vốn có URL). */}
              <a
                href={rawLinkHrefFor(o.id, { rawPhotoLinks: o.rawPhotoLinks }, l) ?? l}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate text-[var(--color-info-text)] underline"
              >
                Link {i + 1} — {rawLinkLabel(l)}
              </a>
              <button
                type="button"
                aria-label="Xóa link"
                onClick={() => setLinks((prev) => prev.filter((_, idx) => idx !== i))}
                disabled={lockedRawLinks}
                className="shrink-0 px-1 text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-1.5">
        <input
          type="url"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={lockedRawLinks}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addDraft();
            }
          }}
          placeholder="Dán link gốc (Drive, Dropbox…)"
          className="h-8 min-w-0 flex-1 rounded-md border bg-surface px-2 text-xs"
        />
        <button
          type="button"
          onClick={addDraft}
          disabled={lockedRawLinks}
          className="h-8 shrink-0 rounded-md border px-2.5 text-xs font-semibold hover:bg-muted/40"
        >
          + Thêm
        </button>
      </div>

      {lockedRawLinks && rawLinksNotice ? <LockNote text={rawLinksNotice} /> : null}

      {dirty && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => void persist(links)}
            disabled={save.isPending}
            className="rounded-md bg-[var(--color-info)] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
          >
            {save.isPending ? "Đang lưu…" : "Lưu thông tin"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Khối chữ vàng nói vì sao một ô đang đóng.
 *
 * Hai ô khoá vì hai lý do KHÁC nhau (ghi chú/thẻ theo trạng thái ca, link ảnh
 * gốc theo ngày chụp) nên mỗi ô có câu riêng — quyết định 0196: "câu giải
 * thích khi ô đóng đi theo ĐÚNG lý do đóng, không dùng chung một câu".
 */
function LockNote({ text }: { text: string }) {
  return (
    <div className="mt-2 rounded-md border border-[var(--color-warning)]/40 bg-[var(--color-warning-bg)] px-2 py-1.5 text-[11px] font-medium text-[var(--color-warning-text)]">
      {text}
    </div>
  );
}

/** Một người trong ô nhân sự — id để tra đúng ảnh, tên để hiện. */
type EkipStaffRef = { id?: string | undefined; name: string };

/**
 * Ê-kíp một vai từ mảng ĐẦY ĐỦ BE trả (photographers/makeups/retouchers —
 * đã gồm cả người chính ở phần tử [0]), bỏ ô trống và chuỗi "---" của FE cũ.
 *
 * Trước migration 0194, BE tách riêng người chính (photographerId/Name...)
 * khỏi phần còn lại (extraPhotographers...) vì cột phẳng chỉ giữ được một
 * người. Cột phẳng đã DROP nên khuôn tách đó không còn căn cứ — giờ nhận
 * thẳng mảng, không cần tự ghép chính + phụ nữa (Quyết định 16).
 */
function staffRefs(members: { id: string; name: string }[] | undefined): EkipStaffRef[] {
  return (members ?? []).filter((r) => r.name !== "" && r.name !== "---");
}

/** Sale không có mảng ê-kíp (BE chỉ trả tên, không có id) — giữ dạng một
 * người riêng, tra ảnh theo tên thay vì id. */
function soloRef(name: string | undefined): EkipStaffRef[] {
  return name && name !== "" && name !== "---" ? [{ id: undefined, name }] : [];
}

function InfoGrid({ order: o }: { order: EkipShift }) {
  // QĐ 0132 câu 2b: nhóm kỹ thuật không thấy SĐT / thư khách — bỏ hẳn hai
  // dòng chứ không vẽ "SĐT: —".
  const { customerContact } = useDataVisibility();
  // Kiểm thử toàn vai 16/09/2026 (B7): ô chi tiết ca in mã máy — "at_basic",
  // "sg_32", "2026-09-20", "booked". Đọc tên từ đúng các nguồn trang Đơn hàng
  // dùng; mã lạ vẫn hiện nguyên văn thay vì ô trống.
  const productNames = useProductNameMap();
  const lineLabel = useLineLabel();
  const branchName = useBranchLabel();
  // Concept có tên thì lên khối riêng (kèm ảnh) phía trên bảng; chưa chọn thì
  // vẫn giữ dòng trong bảng để phân biệt "chưa chọn" với "màn quên vẽ".
  const conceptName = useConceptView().nameOf(o.selectedConcept);
  const photogRefs = staffRefs(o.photographers);
  const makeupRefs = staffRefs(o.makeups);
  const retoucherRefs = staffRefs(o.retouchers);
  // Lịch ekip không mang saleId, nên ô ảnh của Sale tra theo tên.
  const saleRefs = soloRef(o.saleName);
  const photog = photogRefs.map((r) => r.name).join(", ");
  const makeup = makeupRefs.map((r) => r.name).join(", ");
  const retoucher = retoucherRefs.map((r) => r.name).join(", ");

  // Ô giá trị nhận cả node: dòng Concept vẽ ảnh nhỏ + tên — thợ chụp dựng lại
  // buổi chụp theo ẢNH, tên concept không tả được ánh sáng và tông màu.
  // Phần tử thứ tư: dòng người bán — người ngoài Phòng KD thấy tiểu đội (QĐ 0485).
  const cells: Array<[string, ReactNode, EkipStaffRef[]?, boolean?]> = [
    ["Sản phẩm", productNames[o.product] ?? o.product],
    ["Loại", lineLabel(o.category)],
    ["Chi nhánh", branchName(o.branch, { full: true })],
    ...(customerContact
      ? ([
          ["SĐT", o.phone],
          ["Email", o.email],
        ] as Array<[string, string | undefined | null]>)
      : []),
    ["Giới tính", o.gender === "male" ? "Nam" : o.gender === "female" ? "Nữ" : o.gender],
    ["Ngày chụp", o.shootDate ? formatDate(o.shootDate) : ""],
    ["Giờ", `${o.startTime ?? ""}${o.endTime ? ` → ${o.endTime}` : ""}`],
    ["Thợ chụp", photog, photogRefs],
    ["Make up", makeup, makeupRefs],
    ["Retoucher", retoucher, retoucherRefs],
    ["Sale", o.saleName, saleRefs, true],
    ...(conceptName ? [] : ([["Concept", ""]] as Array<[string, ReactNode]>)),
    ["Trạng thái", STATUS_MAP[o.status] ?? o.status],
  ];

  return (
    <div className="space-y-2">
      {conceptName && <ConceptBlock conceptId={o.selectedConcept} />}
      <div className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
      {cells.map(([k, v, refs, isSale], i) => {
        const cell =
          refs && refs.length > 0 ? (
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 font-medium text-foreground">
              {refs.map((r, j) => (
                <span key={`${r.id ?? ""}-${r.name}-${j}`} className="inline-flex items-center gap-1">
                  <StaffAvatar staffId={r.id} name={r.name} sizePx={18} />
                  {r.name}
                </span>
              ))}
            </span>
          ) : (
            <span className="font-medium text-foreground">{v || "—"}</span>
          );
        return (
          <div key={i} className="flex gap-1.5">
            <span className="min-w-[72px] shrink-0 text-muted-foreground">{k}:</span>
            {isSale ? (
              <SaleOrSquad
                orderId={o.id}
                size={18}
                className="min-w-0 font-medium text-foreground"
              >
                {cell}
              </SaleOrSquad>
            ) : (
              cell
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}

function Row({ k, v, success = false }: { k: string; v: string; success?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span
        className={cn(
          "font-semibold",
          success ? "text-[var(--color-success-text)]" : "text-foreground",
        )}
      >
        {v}
      </span>
    </div>
  );
}

// ════════════════ Action buttons (Nhận ca / Hoàn tất) ════════════════

function ActionButtons({ order: o }: { order: EkipShift }) {
  const acceptM = useAcceptShift();
  const completeM = useCompleteShift();
  const myStaffId = useAuth((s) => s.currentStaff)?.id ?? "";
  // Ảnh thẻ (QĐ 0234, CEO chốt 1a · 2a · 3a): được xếp vào ca là đã nhận ca —
  // thợ chụp, thợ trang điểm và cộng tác viên đều không còn nút Nhận ca.
  const acceptOnAssign = useCrewAcceptOnAssign()(o.category);
  const canWorkMakeup = canMakeupWorkShift(o.status, o.makeupProofDueAt);
  // Cửa sớm (QĐ 0261): dòng tự nhận ca, đơn còn "Đã đặt" mà đã tới ngày chụp —
  // nộp đủ cặp ảnh là đơn tự sang "Đang chụp".
  const makeupEarly =
    !o.isPhoto && canMakeupUploadBeforeShoot(o.status, o.shootDate, acceptOnAssign);
  // Chỉ hỏi sổ bằng chứng khi đúng là ca makeup của mình đang mở — tránh
  // gọi thừa cho mọi thẻ ca khác.
  const { data: proofs = [] } = useOrderProofs(
    o.isMine && !o.isPhoto && (canWorkMakeup || makeupEarly) ? o.id : null,
  );
  const pairDone = hasMakeupPair(proofs, myStaffId);

  if (!o.isMine) return null;

  // Thợ chụp + đơn ảnh thẻ đã hậu kỳ xong → nút giao kèm bằng chứng. Đặt
  // TRƯỚC nhánh myConfirm: thợ đã xác nhận xong ca chụp từ trước nên
  // myConfirm luôn truthy, để sau thì không bao giờ chạy tới.
  if (
    o.isPhoto &&
    o.status === "completed" &&
    isIdPhotoOrder({ category: o.category, product: o.product })
  ) {
    return <DeliverBlock orderId={o.id} />;
  }

  if (o.myConfirm) {
    // Ca chỉ sang sửa hình khi cả ekip cùng xác nhận. Xong phần mình mà đơn
    // vẫn "đang chụp" nghĩa là còn người chưa bấm — nói rõ để khỏi tưởng lỗi.
    const waitingForCrew = o.status === "shooting";
    return (
      <div className="mt-2.5 rounded-md border border-[var(--color-success)]/30 bg-[var(--color-success-bg)] p-2.5">
        <div className="text-xs font-bold text-[var(--color-success-text)]">
          Đã xác nhận — {o.isPhoto ? "Photo" : "Makeup"}
        </div>
        <div className="mt-0.5 text-[10px] text-[var(--color-success-text)]/80">
          Xác nhận lúc {new Date(o.myConfirm).toLocaleString("vi-VN")}
        </div>
        {waitingForCrew ? (
          <div className="mt-1 text-[10px] font-semibold text-[var(--color-warning-text)]">
            Chờ thành viên còn lại của ekip xác nhận rồi đơn mới sang sửa hình
          </div>
        ) : null}
      </div>
    );
  }

  if (!o.myAccept && !acceptOnAssign) {
    return (
      <div className="mt-2.5 rounded-md border-2 border-[var(--color-warning)] bg-gradient-to-br from-[var(--color-warning-bg)] to-[var(--color-warning-bg)]/40 p-3">
        <div className="mb-2 text-xs font-bold text-[var(--color-warning-text)]">
          Bạn được phân công {o.isPhoto ? "Photo" : "Makeup"} cho ca này
        </div>
        <button
          type="button"
          onClick={() =>
            acceptM.mutate(o.id, {
              onError: (err: unknown) =>
                toast.error(err instanceof Error ? err.message : "Nhận ca thất bại"),
            })
          }
          disabled={acceptM.isPending}
          className="rounded-md border-0 bg-gradient-to-br from-[var(--color-warning)] to-[var(--color-warning)]/80 px-6 py-2 text-sm font-bold text-white shadow disabled:opacity-60"
        >
          {acceptM.isPending ? "Đang xử lý…" : "Nhận ca"}
        </button>
      </div>
    );
  }

  // Cửa sớm (QĐ 0261) đi CÙNG khối với ca đang chụp: nộp đủ ảnh rồi bấm
  // "Hoàn tất makeup" là máy chủ chuyển đơn sang Đang chụp.
  if (isShiftOpen(o.status) || (!o.isPhoto && canWorkMakeup) || makeupEarly) {
    return (
      <div className="mt-2.5 rounded-md border border-[var(--color-success)]/30 bg-[var(--color-success-bg)]/40 p-3">
        <div className="mb-1.5 text-xs font-bold text-[var(--color-success-text)]">
          Đã nhận ca — {o.isPhoto ? "Photo" : "Makeup"}
        </div>
        {!o.isPhoto ? (
          <>
            <BeforeAfterBlock orderId={o.id} myStaffId={myStaffId} />
            {/* Ô "số mặt tôi đánh" (QĐ 0194 điều 5). Tự ẩn ở buổi không ghi sổ
                số mặt, nên không cần nhánh điều kiện ở đây. */}
            <MyMakeupFacesBlock orderId={o.id} myStaffId={myStaffId} />
            {!pairDone ? (
              <div className="mt-1.5 text-[10px] font-semibold text-[var(--color-warning-text)]">
                Chụp đủ ảnh trước và sau trang điểm mới hoàn tất được ca
              </div>
            ) : null}
            {makeupEarly ? <MakeupEarlyHint /> : null}
          </>
        ) : null}
        <div className="mt-2 border-t border-[var(--color-success)]/30 pt-2">
          <PhotoConfirmButton
            isPhoto={!!o.isPhoto}
            disabled={completeM.isPending || (!o.isPhoto && !pairDone)}
            onConfirm={() =>
              completeM.mutate(o.id, {
                onError: (err: unknown) =>
                  toast.error(err instanceof Error ? err.message : "Hoàn tất thất bại"),
              })
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2.5 rounded-md border border-[var(--color-info)]/30 bg-[var(--color-info-bg)] p-2.5">
      <div className="text-xs font-bold text-[var(--color-info-text)]">
        Đã nhận ca — {o.isPhoto ? "Photo" : "Makeup"}
      </div>
      <div className="mt-0.5 text-[10px] text-[var(--color-info-text)]/80">
        {o.myAccept
          ? `Nhận lúc ${new Date(o.myAccept).toLocaleString("vi-VN")}`
          : "Được xếp vào ca là đã nhận ca"}
      </div>
    </div>
  );
}

/**
 * DeliverBlock — thợ chụp trả ảnh thẻ tận tay khách. Chưa có ảnh bàn giao và
 * khách chưa ký thì bắt chụp trước, giao xong mới chuyển đơn sang "đã giao"
 * (server chặn lần nữa). Nút dùng chung với dải tiến trình trong đơn
 * (DeliverButton) — một luật, hai chỗ bấm.
 */
function DeliverBlock({ orderId }: { orderId: string }) {
  const needsPhoto = useDeliverNeedsPhoto(orderId);
  return (
    <div className="mt-2.5 rounded-md border-2 border-[var(--color-success)] bg-[var(--color-success-bg)]/50 p-3">
      <div className="mb-2 text-xs font-bold text-[var(--color-success-text)]">
        {needsPhoto
          ? "Ảnh đã xong — chụp lại bằng chứng khi trao cho khách"
          : "Ảnh đã xong — bấm khi đã trao ảnh cho khách"}
      </div>
      <DeliverButton orderId={orderId} />
    </div>
  );
}

// ════════════════ Upcoming preview (when no day selected) ════════════════

function UpcomingPreview({ weekDays, today }: { weekDays: EkipDayGroup[]; today: string }) {
  const { nameOf: conceptName } = useConceptView();
  const lineLabel = useLineLabel();
  const upcoming = weekDays.filter((wd) => wd.count > 0 && wd.date !== today).slice(0, 3);
  if (upcoming.length === 0) return null;
  return (
    <div className="mt-3 border-t border-border pt-2">
      <div className="mb-1.5 text-[10px] font-bold text-muted-foreground">Sắp tới:</div>
      {upcoming.map((wd) => (
        <div key={wd.date} className="mb-1">
          <div className="text-[11px] font-bold text-muted-foreground">
            {wd.label} — {wd.day}/{monthOf(wd.date)}
          </div>
          {wd.orders.slice(0, 3).map((o) => (
            <div key={o.id} className="pl-2 text-[10px] text-muted-foreground/80">
              {o.startTime ?? "—"} • {o.customer} •{" "}
              {conceptName(o.selectedConcept) || lineLabel(o.category)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
